import axios from "axios";
import { api, affiliateApi, brandApi } from "../../lib/axios";

export interface ForgotPasswordResponse {
  message: string;
  requiresSelection?: boolean;
  shops?: Array<{
    sessionId: string;
    shop: string;
    companyName: string;
  }>;
}

export interface VerifyOtpResponse {
  message: string;
  resetToken: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface ChangeEmailResponse {
  message: string;
}

export class PasswordService {
  async forgotPassword(
    email: string,
    userType: "affiliate" | "brand"
  ): Promise<ForgotPasswordResponse> {
    try {
      const response = await api.post("/password/forgot", {
        email,
        userType,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to send password reset email"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async forgotPasswordForShop(
    email: string,
    sessionId: string,
    userType: "affiliate" | "brand"
  ): Promise<ForgotPasswordResponse> {
    try {
      const response = await api.post("/password/forgot/shop", {
        email,
        sessionId,
        userType,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to send password reset email"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async verifyOtp(
    email: string,
    otp: string,
    userType: "affiliate" | "brand",
    sessionId?: string
  ): Promise<VerifyOtpResponse> {
    try {
      const response = await api.post("/password/verify-otp", {
        email,
        otp,
        userType,
        ...(sessionId && { sessionId }),
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to verify OTP"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async resetPassword(
    resetToken: string,
    newPassword: string
  ): Promise<ResetPasswordResponse> {
    try {
      const response = await api.post("/password/reset", {
        resetToken,
        newPassword,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to reset password"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    userType: "affiliate" | "brand"
  ): Promise<ChangePasswordResponse> {
    //   if userType is affiliate, use affiliateApi, if userType is brand, use brandApi
    const api = userType === "affiliate" ? affiliateApi : brandApi;
    console.log(userType);

    try {
      const response = await api.post("/password/change", {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to change password"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async changeEmail(
    currentPassword: string,
    newEmail: string,
    userType: "affiliate" | "brand"
  ): Promise<ChangeEmailResponse> {
    try {
      const response = await api.post("/email/change", {
        currentPassword,
        newEmail,
        userType,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to change email"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }
}

export const passwordService = new PasswordService();
